import React from 'react'
import styled from 'styled-components'
import ScrollAnimation from 'react-animate-on-scroll'

import { white } from 'styles/colors'
import { media } from 'styles/media'
import { H2, H3, Lead, fontWeights } from 'styles/typography'
import * as spacing from 'styles/spacing'

import Section, { SectionBlock } from 'components/Section'
import Wrapper from 'components/Wrapper'
import { NativeAuthConsumer } from 'components/NativeAuth'

const Container = styled.div`
  position: static;
  text-align: center;
  color: ${white};
  padding-bottom: 2rem;

  ${media.xLarge`
    padding-bottom: 3rem;
  `};
`

const List = styled.div`
  display: grid;
  grid-gap: ${spacing.large};
  margin-top: ${spacing.small};
  counter-reset: steps;

  ${media.small`
    grid-gap: ${spacing.xLarge};
  `};

  ${media.medium`
    grid-template-columns: repeat(3, 1fr);
    margin-top: ${spacing.medium};
  `};
`

const NumberWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing.medium};
`

const Heading = styled(H2)`
  color: ${white};
  margin-bottom: 0.5em;
  text-transform: uppercase;
  font-weight: ${fontWeights.regular};
  letter-spacing: 0.03em;
`

const Step = styled.div`
  position: static;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
  counter-increment: steps;

  ${media.medium`
    max-width: 360px;
  `};
`

const StepHeading = styled(H3)`
  color: ${white};
  text-transform: uppercase;
`

const Label = styled(Lead)`
  color: ${white};

  button {
    text-decoration: underline;
    font-weight: ${fontWeights.medium};
  }
`

const Number = styled.span`
  display: block;
  font-size: 72px;
  -webkit-text-fill-color: transparent;
  -webkit-text-stroke: 1px ${white};
  font-weight: ${fontWeights.bold};
  line-height: 1;
  margin-right: ${spacing.medium};

  ${media.small`
    margin-right: ${spacing.large};
  `};

  ${media.medium`
    font-size: 80px;
  `};

  ${media.large`
    font-size: 100px;
    margin-right: ${spacing.xLarge};
  `};

  ${media.xLarge`
    font-size: 120px;
  `};
`

const Steps = () => (
  <Container id="how-it-works">
    <Section>
      <Wrapper>
        <SectionBlock>
          <Heading as={ScrollAnimation} animateIn="fadeInUp" offset={0} animateOnce>
            Here's how it works
          </Heading>
        </SectionBlock>
        <SectionBlock>
          <NativeAuthConsumer>
            {({ user, setSignupModalVisibility, setLoginModalVisibility }) => {
              return user ? (
                <List>
                  <Step as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={200} animateOnce>
                    <NumberWrap>
                      <Number>01</Number>
                      <StepHeading>Refer</StepHeading>
                    </NumberWrap>
                    <Label>Invite your friends to get $20 in savings through your personalized link.</Label>
                  </Step>
                  <Step as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={400} animateOnce>
                    <NumberWrap>
                      <Number>02</Number>
                      <StepHeading>Redeem</StepHeading>
                    </NumberWrap>
                    <Label>
                      14 days after your friend makes a purchase of above $25, you’ll receive your own $20 in savings.
                    </Label>
                  </Step>
                  <Step as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={600} animateOnce>
                    <NumberWrap>
                      <Number>03</Number>
                      <StepHeading>Shop</StepHeading>
                    </NumberWrap>
                    <Label>Use your savings on any purchase you make on vuse.com.</Label>
                  </Step>
                </List>
              ) : (
                <List>
                  <Step as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={200} animateOnce>
                    <NumberWrap>
                      <Number>01</Number>
                      <StepHeading>Log in</StepHeading>
                    </NumberWrap>
                    <Label>
                      <button
                        onClick={() => {
                          setLoginModalVisibility(true)
                        }}
                      >
                        Log in to your account
                      </button>{' '}
                      and make sure you've made a purchase. Don't have an account yet?{' '}
                      <button
                        onClick={() => {
                          setSignupModalVisibility(true)
                        }}
                      >
                        Create one
                      </button>{' '}
                      here to get started.
                    </Label>
                  </Step>
                  <Step as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={400} animateOnce>
                    <NumberWrap>
                      <Number>02</Number>
                      <StepHeading>Invite</StepHeading>
                    </NumberWrap>
                    <Label>
                      Share with friends by sending them your personalised link and invite them to earn $20 in savings.
                    </Label>
                  </Step>
                  <Step as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={600} animateOnce>
                    <NumberWrap>
                      <Number>03</Number>
                      <StepHeading>Redeem</StepHeading>
                    </NumberWrap>
                    <Label>
                      Your friends and family will earn a $20 credit after they've made a purchase (and you'll get one
                      too) !
                    </Label>
                  </Step>
                </List>
              )
            }}
          </NativeAuthConsumer>
        </SectionBlock>
      </Wrapper>
    </Section>
  </Container>
)

export default Steps
